import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations } from '../data/translations';
import { useGeoCountry } from './GeoCountryContext';

const LanguageContext = createContext();
const STORAGE_KEY = 'preferredLanguage';
const SUPPORTED = ['en', 'es', 'ru', 'de', 'fr', 'it', 'pl', 'nl', 'pt'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/**
 * Read the URL path prefix (`/en/`, `/es/`, ...) and return the active
 * language code if one is found. SSR-safe (returns null on server).
 */
const detectLangFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const seg = (window.location.pathname || '/').split('/')[1];
  return SUPPORTED.includes(seg) ? seg : null;
};

/**
 * Resolve the initial language with this priority:
 *  1. URL path prefix `/<lang>/...`  (subdirectory routing)
 *  2. Saved manual preference in localStorage (`preferredLanguage`)
 *  3. Browser language: 'es*' → es, 'ru*' → ru, etc., anything else → en
 *  4. English fallback
 */
const resolveInitialLanguage = () => {
  if (typeof window === 'undefined') return 'es';
  const fromUrl = detectLangFromUrl();
  if (fromUrl) return fromUrl;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (_) { /* localStorage may be blocked */ }

  const candidates = [];
  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
    if (navigator.language) candidates.push(navigator.language);
  }
  for (const raw of candidates) {
    const lc = (raw || '').toLowerCase();
    if (lc.startsWith('es')) return 'es';
    if (lc.startsWith('ru')) return 'ru';
    if (lc.startsWith('de')) return 'de';
    if (lc.startsWith('fr')) return 'fr';
    if (lc.startsWith('it')) return 'it';
    if (lc.startsWith('pl')) return 'pl';
    if (lc.startsWith('nl')) return 'nl';
    if (lc.startsWith('pt')) return 'pt';
    if (lc.startsWith('en')) return 'en';
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(resolveInitialLanguage);
  const geo = useGeoCountry();

  // Sync the chosen language into <html lang> for SEO/a11y on every change.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  // Re-detect language from URL on history changes (back / forward / pushState).
  // When the path prefix changes (e.g. user navigates from `/en/products` to
  // `/de/products`), the active language must follow.
  useEffect(() => {
    const sync = () => {
      const fromUrl = detectLangFromUrl();
      if (fromUrl && fromUrl !== currentLanguage) {
        setCurrentLanguage(fromUrl);
      }
    };
    window.addEventListener('popstate', sync);
    // pushState/replaceState don't fire popstate; patch them once.
    if (!window.__plpHistoryPatched) {
      const origPush = window.history.pushState;
      const origReplace = window.history.replaceState;
      window.history.pushState = function () {
        const r = origPush.apply(this, arguments);
        window.dispatchEvent(new Event('plp:locationchange'));
        return r;
      };
      window.history.replaceState = function () {
        const r = origReplace.apply(this, arguments);
        window.dispatchEvent(new Event('plp:locationchange'));
        return r;
      };
      window.__plpHistoryPatched = true;
    }
    window.addEventListener('plp:locationchange', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('plp:locationchange', sync);
    };
  }, [currentLanguage]);

  /**
   * Translate a key. Interpolates the `{loc}` placeholder with the visitor's
   * detected "in {country}" phrase so location-aware copy adapts dynamically
   * (e.g. "in Germany" / "en Alemania" / "в Германии"). Falls back to a
   * neutral "worldwide" phrase when geo detection fails.
   */
  const t = (key) => {
    const raw = translations[currentLanguage]?.[key] || translations['en']?.[key] || key;
    if (typeof raw !== 'string' || !raw.includes('{loc}')) return raw;
    const localeFallback = {
      es: 'a nivel mundial',
      ru: 'по всему миру',
      de: 'weltweit',
      fr: 'dans le monde entier',
      it: 'in tutto il mondo',
      pl: 'na całym świecie',
      nl: 'wereldwijd',
      pt: 'no mundo todo',
    };
    const inPhrase = geo?.in_phrase?.[currentLanguage]
      || localeFallback[currentLanguage] || 'worldwide';
    return raw.replace('{loc}', inPhrase);
  };

  /**
   * Manually change the language. Persists the choice in localStorage so
   * subsequent visits skip the browser-language detection step.
   */
  const changeLanguage = (language) => {
    if (!SUPPORTED.includes(language)) return;
    setCurrentLanguage(language);
    try { window.localStorage.setItem(STORAGE_KEY, language); } catch (_) {}
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      changeLanguage,
      t,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
